/**
 * Bridge to the Android "NativePianoAudio" Capacitor plugin
 * (low-latency AudioTrack synth). No-ops on web / Electron / iOS.
 */

import { Capacitor, registerPlugin } from "@capacitor/core";

export interface NativePianoAudioInfo {
  backend: "audiotrack" | "none";
  sampleRate: number;
  bufferFrames: number;
}

interface NativePianoAudioPlugin {
  start(): Promise<NativePianoAudioInfo>;
  noteOn(options: { midi: number; velocity?: number }): Promise<void>;
  noteOff(options: { midi: number }): Promise<void>;
  allNotesOff(): Promise<void>;
  setMasterVolume(options: { value: number }): Promise<void>;
  setMuted(options: { value: boolean }): Promise<void>;
  getInfo(): Promise<NativePianoAudioInfo>;
}

const NativePianoAudio = registerPlugin<NativePianoAudioPlugin>("NativePianoAudio");

export function isNativePianoAudioAvailable(): boolean {
  try {
    return (
      Capacitor.isNativePlatform() &&
      Capacitor.getPlatform() === "android" &&
      Capacitor.isPluginAvailable("NativePianoAudio")
    );
  } catch {
    return false;
  }
}

/** Start (or confirm) the native engine. Returns null when unavailable. */
export async function nativePianoStart(): Promise<NativePianoAudioInfo | null> {
  if (!isNativePianoAudioAvailable()) return null;
  try {
    return await NativePianoAudio.start();
  } catch {
    return null;
  }
}

/** Fire-and-forget for minimal key-press latency. */
export function nativePianoNoteOn(midi: number, velocity = 100): void {
  if (!isNativePianoAudioAvailable()) return;
  void NativePianoAudio.noteOn({ midi, velocity }).catch(() => {});
}

export function nativePianoNoteOff(midi: number): void {
  if (!isNativePianoAudioAvailable()) return;
  void NativePianoAudio.noteOff({ midi }).catch(() => {});
}

export function nativePianoAllNotesOff(): void {
  if (!isNativePianoAudioAvailable()) return;
  void NativePianoAudio.allNotesOff().catch(() => {});
}

export function nativePianoSetMasterVolume(value: number): void {
  if (!isNativePianoAudioAvailable()) return;
  void NativePianoAudio.setMasterVolume({ value }).catch(() => {});
}

export function nativePianoSetMuted(value: boolean): void {
  if (!isNativePianoAudioAvailable()) return;
  void NativePianoAudio.setMuted({ value }).catch(() => {});
}

export async function nativePianoGetInfo(): Promise<NativePianoAudioInfo | null> {
  if (!isNativePianoAudioAvailable()) return null;
  try {
    return await NativePianoAudio.getInfo();
  } catch {
    return null;
  }
}
